import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Paper
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import api from "../../services/api";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(3),
    },
    minWidth: "auto",
    [theme.breakpoints.up("sm")]: {
      minWidth: 400,
    },
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      gap: theme.spacing(2),
    },
  },
  button: {
    padding: theme.spacing(1.2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(1.5),
    },
    textTransform: "none",
    fontSize: "0.9rem",
    [theme.breakpoints.up("sm")]: {
      fontSize: "1rem",
    },
  },
  inputContainer: {
    marginTop: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(1.5),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2),
    },
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.up("sm")]: {
      marginBottom: theme.spacing(2),
    },
  },
  infoPaper: {
    padding: theme.spacing(1.5),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2),
    },
    textAlign: "center",
  },
}));

const DiskQuotaLimitModal = ({ open, onClose, companyId, diskUsage, diskQuota, isBlocked = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [daysToKeep, setDaysToKeep] = useState(30);
  const [loading, setLoading] = useState(false);
  const [showCleanupInput, setShowCleanupInput] = useState(false);
  // Buscar número de suporte da variável de ambiente
  const supportNumber = process.env.REACT_APP_NUMBER_SUPPORT?.trim() || null;
  
  const isDarkMode = theme.palette.type === 'dark';
  

  useEffect(() => {
    // Buscar configuração salva de dias para manter
    const fetchDaysToKeep = async () => {
      try {
        const { data } = await api.get("/companySettingOne", {
          params: { column: "diskCleanupDaysToKeep" }
        });
        if (data && data.value) {
          setDaysToKeep(parseInt(data.value, 10) || 30);
        }
      } catch (error) {
        // Ignorar erro, usar padrão
      }
    };
    fetchDaysToKeep();
  }, []);

  const handleStartCleanup = async () => {
    if (!daysToKeep || daysToKeep < 1 || daysToKeep > 365) {
      toast.error("O número de dias deve estar entre 1 e 365");
      return;
    }

    setLoading(true);
    try {
      await api.post(`/companies/${companyId}/disk-cleanup`, {
        daysToKeep
      });
      
      toast.success("Limpeza de disco iniciada! Acompanhe o progresso no canto da tela.");
      setShowCleanupInput(false);
      onClose();
      // Notificar o layout para mostrar o componente de progresso
      window.dispatchEvent(new CustomEvent('showCleanupProgress', { detail: { companyId } }));
    } catch (error) {
      const errorMsg = error.response?.data?.error || "Erro ao iniciar limpeza de disco";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSupport = async () => {
    if (!supportNumber) {
      toast.error("Número de suporte não configurado. Configure a variável REACT_APP_NUMBER_SUPPORT no arquivo .env do frontend.");
      return;
    }

    setLoading(true);
    try {
      // Buscar ou criar contato de suporte
      let contact;
      const supportNumberClean = supportNumber.replace(/\D/g, ""); // Remove caracteres não numéricos
      
      try {
        // Tentar buscar contato existente usando searchParam
        const { data: contactResponse } = await api.get("/contacts", {
          params: { 
            searchParam: supportNumberClean,
            pageNumber: 1
          }
        });
        
        // A resposta pode ser {contacts: [], count, hasMore} ou array direto
        const contacts = contactResponse?.contacts || contactResponse || [];
        contact = Array.isArray(contacts) && contacts.length > 0 
          ? contacts.find(c => c.number === supportNumberClean || c.number?.replace(/\D/g, "") === supportNumberClean)
          : null;
        
        if (!contact) {
          // Criar novo contato se não existir
          const { data: newContact } = await api.post("/contacts", {
            name: "Suporte",
            number: supportNumberClean,
            companyId,
            email: ""
          });
          contact = newContact;
        }
      } catch (error) {
        // Se não conseguir buscar/criar, tentar criar diretamente
        try {
          const { data: newContact } = await api.post("/contacts", {
            name: "Suporte",
            number: supportNumberClean,
            companyId,
            email: ""
          });
          contact = newContact;
        } catch (createError) {
          throw new Error("Não foi possível criar o contato de suporte");
        }
      }

      if (!contact || !contact.id) {
        throw new Error("Contato de suporte não encontrado");
      }

      // Buscar WhatsApp padrão ou usar o do usuário
      let whatsappId = user?.whatsappId || null;
      if (!whatsappId) {
        try {
          const { data: whatsapps } = await api.get("/whatsapp", {
            params: { companyId, session: 0 }
          });
          if (whatsapps && whatsapps.length > 0) {
            whatsappId = whatsapps[0].id;
          }
        } catch (error) {
          // Continuar sem whatsappId se não conseguir buscar
        }
      }

      // Buscar fila padrão do usuário ou primeira fila disponível
      let queueId = null;
      if (user?.queues && user.queues.length > 0) {
        queueId = user.queues[0].id;
      } else {
        try {
          const { data: queues } = await api.get("/queue", {
            params: { companyId }
          });
          if (queues && queues.length > 0) {
            queueId = queues[0].id;
          }
        } catch (error) {
          // Continuar sem queueId se não conseguir buscar
        }
      }

      // Criar ticket
      const { data: ticket } = await api.post("/tickets", {
        contactId: contact.id,
        queueId,
        whatsappId,
        userId: user?.id,
        status: "open",
      });

      // Mensagem inicial no ticket
      const messageBody = `Olá,\n\nGostaria de solicitar um aumento no limite de armazenamento do meu plano.\n\n` +
        `Empresa ID: ${companyId}\n` +
        `Uso atual: ${diskUsage?.formattedDiskUsage || "N/A"} de ${diskUsage?.formattedDiskQuota || "N/A"}\n` +
        `Porcentagem: ${diskUsage?.percentage || 0}%\n\n` +
        `Aguardo retorno.\n\nAtenciosamente`;

      // Enviar mensagem inicial no ticket
      try {
        await api.post("/messages", {
          ticketId: ticket.id,
          body: messageBody,
          read: true
        });
      } catch (msgError) {
        // Continuar mesmo se não conseguir enviar a mensagem
      }

      toast.success("Ticket de suporte criado com sucesso!");
      onClose();
      
      // Redirecionar para o ticket
      if (ticket && ticket.uuid) {
        history.push(`/tickets/${ticket.uuid}`);
      } else {
        history.push("/tickets");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Erro ao criar ticket de suporte. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const percentage = diskUsage?.percentage || 0;
  const formattedUsage = diskUsage?.formattedDiskUsage || "0 B";
  const formattedQuota = diskUsage?.formattedDiskQuota || "0 B";

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      fullScreen={false}
      PaperProps={{
        style: {
          margin: 16,
        }
      }}
    >
      <DialogTitle style={{ paddingBottom: 8 }}>
        <Typography 
          variant="h6" 
          component="div" 
          style={{ 
            fontSize: "1.1rem",
            color: theme.palette.text.primary
          }}
        >
          {isBlocked ? "Limite de Armazenamento Atingido" : "Aviso de Armazenamento"}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Paper 
          elevation={0} 
          className={classes.paper}
          style={{ 
            backgroundColor: isDarkMode 
              ? (isBlocked ? "rgba(244, 67, 54, 0.15)" : "rgba(255, 152, 0, 0.15)")
              : (isBlocked ? "#f8d7da" : "#fff3cd"), 
            border: isDarkMode
              ? (isBlocked ? "1px solid rgba(244, 67, 54, 0.5)" : "1px solid rgba(255, 152, 0, 0.5)")
              : (isBlocked ? "1px solid #dc3545" : "1px solid #ffc107"),
          }}
        >
          <Typography 
            variant="body2" 
            style={{ 
              fontWeight: "bold", 
              marginBottom: 8, 
              fontSize: "0.95rem",
              color: isDarkMode 
                ? (isBlocked ? "#ff5252" : "#ffb74d")
                : (isBlocked ? "#721c24" : "#856404")
            }}
          >
            {isBlocked ? "🚫 Limite de Armazenamento Atingido" : "⚠️ Aviso de Armazenamento"}
          </Typography>
          <Typography 
            variant="body2" 
            style={{ 
              fontSize: "0.9rem", 
              lineHeight: 1.5,
              color: isDarkMode ? theme.palette.text.primary : "#333"
            }}
          >
            Você está usando <strong>{formattedUsage}</strong> de <strong>{formattedQuota}</strong> ({percentage}%).
          </Typography>
          <Typography 
            variant="body2" 
            style={{ 
              marginTop: 8, 
              fontSize: "0.9rem", 
              lineHeight: 1.5,
              color: isDarkMode ? theme.palette.text.primary : "#333"
            }}
          >
            {isBlocked 
              ? "O limite de armazenamento foi atingido. Não é possível fazer novos uploads até liberar espaço."
              : "Você está próximo do limite de armazenamento. Considere fazer uma limpeza de disco para evitar bloqueios."
            }
          </Typography>
        </Paper>

        {user?.profile === "admin" ? (
          !showCleanupInput ? (
            <Box className={classes.buttonContainer}>
              <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={() => setShowCleanupInput(true)}
                fullWidth
              >
                Limpeza de Disco
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                className={classes.button}
                onClick={handleContactSupport}
                disabled={loading}
                fullWidth
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? "Criando ticket..." : "Solicitar Mais Espaço ao Suporte"}
              </Button>
              {!supportNumber && (
                <Typography variant="caption" style={{ color: "#f44336", marginTop: 8, display: "block", textAlign: "center" }}>
                  ⚠️ Número de suporte não configurado. Configure REACT_APP_NUMBER_SUPPORT no .env
                </Typography>
              )}
            </Box>
          ) : (
          <Box className={classes.inputContainer}>
            <Typography 
              variant="body2" 
              style={{ 
                marginBottom: 16,
                color: theme.palette.text.primary
              }}
            >
              Defina quantos dias de mídias você deseja manter. Arquivos mais antigos serão removidos automaticamente.
            </Typography>
            <TextField
              label="Dias para manter mídias"
              type="number"
              value={daysToKeep}
              onChange={(e) => setDaysToKeep(parseInt(e.target.value, 10) || 30)}
              inputProps={{ min: 1, max: 365 }}
              fullWidth
              variant="outlined"
              helperText="Arquivos com mais de X dias serão removidos (1-365 dias)"
            />
            <Box style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleStartCleanup}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                Iniciar Limpeza
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowCleanupInput(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
            </Box>
          </Box>
          )
        ) : (
          // Usuário comum - apenas mensagem para contatar administrador
          <Box className={classes.buttonContainer}>
            <Paper 
              elevation={0} 
              className={classes.infoPaper}
              style={{ 
                backgroundColor: isDarkMode ? "rgba(33, 150, 243, 0.15)" : "#e3f2fd", 
                border: isDarkMode ? "1px solid rgba(33, 150, 243, 0.5)" : "1px solid #2196f3",
                borderRadius: "8px",
              }}
            >
              <Typography 
                variant="body2" 
                style={{ 
                  fontWeight: "bold", 
                  marginBottom: 8, 
                  color: isDarkMode ? "#64b5f6" : "#1976d2", 
                  fontSize: "0.95rem" 
                }}
              >
                ℹ️ Entre em Contato com o Administrador
              </Typography>
              <Typography 
                variant="body2" 
                style={{ 
                  color: isDarkMode ? theme.palette.text.primary : "#555", 
                  fontSize: "0.9rem", 
                  lineHeight: 1.5 
                }}
              >
                Entre em contato com o administrador da empresa para solucionar o problema de armazenamento.
              </Typography>
            </Paper>
            <Button
              variant="outlined"
              color="secondary"
              className={classes.button}
              onClick={handleContactSupport}
              disabled={loading}
              fullWidth
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Criando ticket..." : "Solicitar Mais Espaço ao Suporte"}
            </Button>
            {!supportNumber && (
              <Typography variant="caption" style={{ color: "#f44336", marginTop: 8, display: "block", textAlign: "center" }}>
                ⚠️ Número de suporte não configurado. Configure REACT_APP_NUMBER_SUPPORT no .env
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiskQuotaLimitModal;

